// Sound panel — per-track mixing controls the later tapes unlock: how much
// of a track's signal goes into the shared reverb/echo ("space") and how its
// filter and envelope shape its tone ("tone"). Reads current values with
// soundOf(track) and reports changes as a patch via onChange.

import React from "react";
import { cn } from "../../lib/utils";
import { INSTRUMENTS, soundOf } from "../../lib/starterTape";

const pct = (v) => Math.round(v * 100);
const seconds = (v) => `${Math.round(v * 100) / 100} s`;

function SliderRow({ label, hint, value, display, min, max, step, onChange }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs uppercase tracking-wide text-zinc-500">
          {label}
        </span>
        <span className="text-xs tabular-nums text-zinc-300">{display}</span>
      </div>
      <input
        type="range"
        aria-label={label}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-amber-300"
      />
      {hint && <p className="text-xs text-zinc-500">{hint}</p>}
    </div>
  );
}

export default function SoundPanel({ track, controls, onChange }) {
  const inst = INSTRUMENTS[track.instrument];
  const sound = soundOf(track);
  const hasSpace = controls.includes("space");
  const hasTone = controls.includes("tone") && !inst.unpitched;
  const hasSwell = hasTone && track.instrument === "pad";

  if (!hasSpace && !hasTone) return null;

  return (
    <div className="mb-4 rounded-xl border border-zinc-800 bg-zinc-900/60 p-3">
      <p className="mb-2 text-sm font-semibold text-zinc-100">
        {inst.name} sound
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {hasSpace && (
          <SliderRow
            label="Reverb"
            hint="How much of the sound goes into the room."
            value={pct(sound.reverb)}
            display={`${pct(sound.reverb)} %`}
            min={0}
            max={100}
            step={1}
            onChange={(v) => onChange({ reverb: v / 100 })}
          />
        )}
        {hasSpace && (
          <SliderRow
            label="Echo"
            hint="Repeats timed to the beat."
            value={pct(sound.delay)}
            display={`${pct(sound.delay)} %`}
            min={0}
            max={100}
            step={1}
            onChange={(v) => onChange({ delay: v / 100 })}
          />
        )}
        {hasTone && (
          <SliderRow
            label="Brightness"
            hint="Dark and far, or bright and close."
            value={pct(sound.brightness)}
            display={`${pct(sound.brightness)} %`}
            min={0}
            max={100}
            step={1}
            onChange={(v) => onChange({ brightness: v / 100 })}
          />
        )}
        {hasSwell && (
          <SliderRow
            label="Swell"
            hint="How slowly each note fades in."
            value={sound.attack}
            display={seconds(sound.attack)}
            min={0.05}
            max={4}
            step={0.05}
            onChange={(v) => onChange({ attack: v })}
          />
        )}
        {hasSwell && (
          <SliderRow
            label="Fade"
            hint="How long each note rings after it ends."
            value={sound.release}
            display={seconds(sound.release)}
            min={0.1}
            max={8}
            step={0.1}
            onChange={(v) => onChange({ release: v })}
          />
        )}
      </div>
      {hasSwell && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => onChange({ sweep: !sound.sweep })}
            aria-pressed={!!sound.sweep}
            className={cn(
              "rounded-md px-2.5 py-1 text-sm font-medium",
              sound.sweep
                ? "bg-amber-300 text-zinc-900"
                : "border border-zinc-700 text-zinc-400 hover:bg-zinc-800"
            )}
          >
            {sound.sweep ? "Sweep on" : "Sweep off"}
          </button>
          <p className="mt-1 text-xs text-zinc-500">
            Opens the filter slowly across the loop.
          </p>
        </div>
      )}
    </div>
  );
}
